/**
 * The arithmetic under the charts: scales, tick steps and SVG path strings.
 *
 * Pure functions with no DOM, so they are tested with plain node and the
 * components stay a thin layer of markup over them. Every number in is a
 * data value or a pixel; every string out is a `d` attribute.
 */

export interface Point { x: number; y: number }

/**
 * A ceiling for the y axis that makes every tick a round number: the step
 * is `max / ticks` rounded up to 1, 2, 2.5 or 5 times a power of ten, and
 * the ceiling is that step times the tick count. 7 → 8, 23 → 40, 140 → 200,
 * 1 234 → 2 000. Anything under four counts gets a scale of four, so the
 * ticks are whole examinations rather than quarters of one.
 */
export function niceMax(max: number, tickCount = 4): number {
  if (!Number.isFinite(max) || max <= tickCount) return tickCount;
  const rough = max / tickCount;
  const base = 10 ** Math.floor(Math.log10(rough));
  const f = rough / base;
  const step = (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * base;
  return step * tickCount;
}

/** Evenly spaced tick values from zero to `max`, inclusive. */
export function ticks(max: number, count = 4): number[] {
  const out: number[] = [];
  for (let i = 0; i <= count; i += 1) out.push((max * i) / count);
  return out;
}

/** Maps a data value in [0, max] onto pixels, top of the plot at `top`. */
export function yScale(max: number, top: number, bottom: number): (v: number) => number {
  const span = bottom - top;
  return (v) => (max <= 0 ? bottom : bottom - (Math.max(0, v) / max) * span);
}

/**
 * The x pixel of each of `n` evenly spaced points between `left` and `right`,
 * first on the left edge and last on the right. One point sits centred.
 */
export function xPositions(n: number, left: number, right: number): number[] {
  if (n <= 0) return [];
  if (n === 1) return [(left + right) / 2];
  const step = (right - left) / (n - 1);
  return Array.from({ length: n }, (_, i) => left + i * step);
}

const px = (v: number) => (Math.round(v * 100) / 100).toString();

/** `M x y L x y …` through the points; an empty list is an empty path. */
export function linePath(points: Point[]): string {
  return points.map((p, i) => `${i === 0 ? 'M' : 'L'}${px(p.x)} ${px(p.y)}`).join(' ');
}

/**
 * A smooth line through the points — the "wave" look (owner, 29 Oct 2026) —
 * by monotone cubic interpolation (Fritsch–Carlson): it bends between the
 * months but never overshoots them, so a month of 0 is never drawn as a dip
 * below zero and a peak is never drawn higher than it was.
 */
export function smoothPath(points: Point[]): string {
  const n = points.length;
  if (n < 3) return linePath(points);
  const dx: number[] = [], m: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx[i] = points[i + 1]!.x - points[i]!.x;
    m[i] = dx[i]! === 0 ? 0 : (points[i + 1]!.y - points[i]!.y) / dx[i]!;
  }
  const t: number[] = [m[0]!];
  for (let i = 1; i < n - 1; i++) t[i] = m[i - 1]! * m[i]! <= 0 ? 0 : (m[i - 1]! + m[i]!) / 2;
  t[n - 1] = m[n - 2]!;
  for (let i = 0; i < n - 1; i++) {
    if (m[i] === 0) { t[i] = 0; t[i + 1] = 0; continue; }
    const a = t[i]! / m[i]!, b = t[i + 1]! / m[i]!, h = a * a + b * b;
    if (h > 9) { const k = 3 / Math.sqrt(h); t[i] = k * a * m[i]!; t[i + 1] = k * b * m[i]!; }
  }
  let d = `M${px(points[0]!.x)} ${px(points[0]!.y)}`;
  for (let i = 0; i < n - 1; i++) {
    const p0 = points[i]!, p1 = points[i + 1]!, h = dx[i]! / 3;
    d += ` C${px(p0.x + h)} ${px(p0.y + t[i]! * h)} ${px(p1.x - h)} ${px(p1.y - t[i + 1]! * h)} ${px(p1.x)} ${px(p1.y)}`;
  }
  return d;
}

/** The smooth line, closed down to `baseline`. */
export function smoothAreaPath(points: Point[], baseline: number): string {
  if (!points.length) return '';
  const first = points[0]!, last = points[points.length - 1]!;
  return `${smoothPath(points)} L${px(last.x)} ${px(baseline)} L${px(first.x)} ${px(baseline)} Z`;
}

/** The line, closed down to `baseline` and back along it. */
export function areaPath(points: Point[], baseline: number): string {
  if (!points.length) return '';
  const first = points[0]!;
  const last = points[points.length - 1]!;
  return `${linePath(points)} L${px(last.x)} ${px(baseline)} L${px(first.x)} ${px(baseline)} Z`;
}

/** Where a clock-angle (0 at twelve, clockwise, in turns 0–1) lands on a circle. */
export function polar(cx: number, cy: number, r: number, turn: number): Point {
  const a = turn * 2 * Math.PI - Math.PI / 2;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

/**
 * A ring segment from `start` to `end` (turns, 0–1), outer radius `r`,
 * inner `ri`. A whole turn is drawn as two halves, because an SVG arc from
 * a point back to itself collapses to nothing.
 */
export function arcPath(cx: number, cy: number, r: number, ri: number, start: number, end: number): string {
  const span = Math.min(1, Math.max(0, end - start));
  if (span <= 0) return '';
  if (span >= 1) {
    return arcPath(cx, cy, r, ri, start, start + 0.5) + ' ' + arcPath(cx, cy, r, ri, start + 0.5, start + 1);
  }
  const large = span > 0.5 ? 1 : 0;
  const o1 = polar(cx, cy, r, start);
  const o2 = polar(cx, cy, r, end);
  const i1 = polar(cx, cy, ri, end);
  const i2 = polar(cx, cy, ri, start);
  return [
    `M${px(o1.x)} ${px(o1.y)}`,
    `A${px(r)} ${px(r)} 0 ${large} 1 ${px(o2.x)} ${px(o2.y)}`,
    `L${px(i1.x)} ${px(i1.y)}`,
    `A${px(ri)} ${px(ri)} 0 ${large} 0 ${px(i2.x)} ${px(i2.y)}`,
    'Z',
  ].join(' ');
}

export interface Segment<T> { item: T; start: number; end: number; share: number }

/**
 * Lays values out around a ring in the order given, skipping zeros. `share`
 * is the fraction of the whole; the segments' ends meet at 1 when any value
 * is positive.
 */
export function donutSegments<T>(items: T[], value: (item: T) => number): Array<Segment<T>> {
  const total = items.reduce((n, it) => n + Math.max(0, value(it)), 0);
  if (total <= 0) return [];
  const out: Array<Segment<T>> = [];
  let at = 0;
  for (const item of items) {
    const v = Math.max(0, value(item));
    if (v <= 0) continue;
    const share = v / total;
    out.push({ item, start: at, end: at + share, share });
    at += share;
  }
  const last = out[out.length - 1];
  if (last) last.end = 1;
  return out;
}

/**
 * Pushes labels apart vertically so none overlap: each is moved down just
 * enough to clear the one above it, in the order given (sorted by `y`
 * first). Returns the new y for each input, in the input order.
 */
export function spreadLabels(ys: number[], minGap: number): number[] {
  const order = ys.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y);
  let floor = -Infinity;
  const out = new Array<number>(ys.length);
  for (const { y, i } of order) {
    const placed = Math.max(y, floor);
    out[i] = placed;
    floor = placed + minGap;
  }
  return out;
}

/** 1 234 → "1,234" in the Indian grouping used across the consoles. */
export function formatCount(n: number): string {
  return n.toLocaleString('en-IN');
}
